/*
 * In the long run:
 *
 * - the Object Store will store each of our data types according to a company-wide JSON schema.
 * - the RMS (or whatever replaces it) will define an API for retrieving objects such objects
 * - the VMS may or may not use the canonical data type for any given object, as long as when we
 *      read and write objects we transform them to the canonical definition.
 *
 * In the short run:
 *
 * - the company-wide schema doesn't exist
 * - data returned by the RMS is perforce not in a canonical format
 * - the RMS API is not really appropriate for the VMS
 *
 * To handle this, we handle potentially multiple transformations:
 *
 * - transforming existing RMS API calls to more canonical calls the VMS would *like* to have available
 * - transforming data from/to the RMS into the canonical format that will *eventually* be stored in the Object Store
 *
 * To formalize this separation:
 *
 * - a BaseLoader makes calls to the RMS (or mocks calls to the RMS)
 * - CanonicalLoader wraps the BaseLoader and transforms the current API and data to canonical form
 *
 */
import { Entity, Feature, Functional, Ordinal, Theme, Level, Structure, Venue } from '@wayfinder/vms-base';
import * as R from 'ramda';
import {
    baseLoader,
    initializeBaseLoader,
    filePathsFromVenueId,
    DEFAULT_LOADERS,
    CONTENT_TYPES,
} from '@wayfinder/vms-loader';
import { convertStyle } from './convert-mapbox-studio-style';
import { getState } from '../store';

// This value used to be import.meta.env.VITE_REACT_APP_MOCK -- but we don't ever set that value anymore. hardcoding to false.
const IS_MOCK = false;

// ---------------------------------------------------------------------------------------------------------------------
//
// THEME
// CATEGORIES
// BADGES
//
// ---------------------------------------------------------------------------------------------------------------------

const initializeLoader = (accountId, getAccessToken, trackJS, stage = 'prod') => {
    const userConfig = { accountId, getAccessToken, trackJS };
    const loaderOptions = R.mergeRight(DEFAULT_LOADERS, {
        stage,
        loaders: { isMock: Boolean(IS_MOCK) },
    });
    initializeBaseLoader(loaderOptions, userConfig);
};

const getInsightsAnalyticsConfig = async () => {
    const config = await baseLoader().generateEmbeddableDashboards();
    return config;
};

const setFilePaths = venueId => {
    if (!IS_MOCK) return;

    const filePaths = filePathsFromVenueId(venueId);
    baseLoader().setFilePaths(filePaths);
};

/*
 * Return the theme--but enrich it with defaults
 * @sig getTheme :: String -> String -> Promise Theme
 */
const getTheme =
    (venueCategory = 'airport') =>
    async venueId => {
        const accountId = baseLoader().accountId();
        const theme = await baseLoader().getContent(
            CONTENT_TYPES.THEME,
            'theme.json',
            venueCategory,
            venueId,
            accountId,
        );
        return Theme.enrichWithDefaults(theme);
    };

// ---------------------------------------------------------------------------------------------------------------------
//
// ENTITIES
//
// ---------------------------------------------------------------------------------------------------------------------

const rms3GetEntities = async (venueId, locale, baseVenueId) => {
    const response = await baseLoader().getEntities(venueId, undefined, 'legacy', locale, baseVenueId);
    return R.propOr([], 'entities', response);
};

/*
 * Return the Venue's entities
 * @sig getEntities :: VenueId -> Promise [Entity]
 */
const getEntities = (isEntityMigrated, locale, baseVenueId) => async venueId => {
    let entities = isEntityMigrated
        ? await rms3GetEntities(venueId, locale, baseVenueId)
        : await baseLoader().listPOIs(venueId);

    entities = R.filter(entity => R.prop('id', entity), entities);
    return Entity.convertGateToLower(entities);
};

/*
 * Return the raw POI data
 */
const listPOIs = async venueId => baseLoader().listPOIs(venueId);

// ---------------------------------------------------------------------------------------------------------------------
//
// FEATURES
//
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Return the features for a single Ordinal of a Venue -- but enrich them
 * @sig getFeaturesForOrdinal :: (VenueId, OrdinalId) -> Promise [Feature]
 */
const getFeaturesForOrdinal = async (venueId, ordinalId, levels, theme, entities, isFeatureMigrated) => {
    const entityIdNameMap = {};
    R.map(e => (entityIdNameMap[e.id] = e.mapLabel || e.name), entities);
    const updateFeatureFromTheme = Feature.updateFeatureFromTheme(theme);
    const updateFeatureFromEntity = Feature.updateFeatureFromEntity(entityIdNameMap);
    const featuresFromOneGeoJson = async levelId => {
        const enrichFeature = feature => {
            feature = Functional.mergeWithProp('properties', { venueId, structureId, ordinalId, levelId }, feature);
            feature = Feature.promoteIdFromSubidProperty(feature);
            feature = updateFeatureFromTheme(feature);
            if (R.hasPath(['properties', 'text'], feature)) {
                feature = updateFeatureFromEntity(feature);
            }
            return feature;
        };

        const structureId = levelId.replace(/-[^-]*$/, '');
        const geoJson = isFeatureMigrated
            ? await baseLoader().getFeatures(venueId, levelId)
            : await baseLoader().getGeoJson(venueId, levelId);
        return R.map(enrichFeature, geoJson.features);
    };

    const levelIds = Level.getLevelIdsForOrdinal(ordinalId, levels);
    const parallelLoads = R.map(featuresFromOneGeoJson, levelIds);
    const featureArrays = await Promise.all(parallelLoads);
    return Functional.concatAll(featureArrays);
};

/*
 * Return the features for the venue's background -- but enrich them
 * @sig getFeaturesForVenueBackground :: VenueId -> Promise [Feature]
 */
const getFeaturesForVenueBackground = theme => async venueId => {
    const updateFeatureFromTheme = Feature.updateFeatureFromTheme(theme);

    const enrichFeature = feature => {
        feature = Functional.mergeWithProp('properties', { venueId, ordinalId }, feature);
        feature = Feature.promoteIdFromSubidProperty(feature);
        feature = updateFeatureFromTheme(feature);
        return feature;
    };

    const ordinalId = Ordinal.LANDSCAPE_BACKGROUND;
    const geoJson = await baseLoader().getGeoJson(venueId, venueId);
    return R.map(enrichFeature, geoJson.features);
};

// ---------------------------------------------------------------------------------------------------------------------
//
// METADATA
//
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Return the Ordinals for a Venue
 * Optionally pass in the venueData for IMDF
 * @sig getOrdinalsForVenue :: VenueId -> [Ordinal]
 */
const getOrdinalsForVenue = async (venueId, format = 'VMS') => {
    const ordinals = await baseLoader().getVenueOrdinals(venueId, format);
    return R.map(Ordinal.from, ordinals);
};

/*
 * Return the Structures for a Venue
 * Optionally pass in the venueData for IMDF
 * @sig getStructuresForVenue :: VenueId -> [Structure]
 */
const getStructuresForVenue = async (venueId, format = 'VMS') => {
    const structures = await baseLoader().getVenueStructures(venueId, format);
    return R.map(Structure.from, structures);
};

/*
 * Return the Levels for a Venue
 * Optionally pass in the venueData for IMDF
 * @sig getLevelsForVenue :: VenueId -> [Level]
 */
const getLevelsForVenue = async (venueId, format = 'VMS') => {
    const levels = await baseLoader().getVenueLevels(venueId, format);
    return R.map(Level.from, levels);
};

/*
 * RMS 3.0 call that checks if a venue has been migrated to object store
 */
const getIsMigrated = async venueId => {
    const isMigrated = await baseLoader().venueIdIsMigrated(venueId);
    return isMigrated;
};

const getVenue = async (venueId, format = 'VMS') => {
    const venue = await baseLoader().getVenue(venueId, format);

    return format === 'VMS' ? Venue.from(venue) : venue;
};

// ---------------------------------------------------------------------------------------------------------------------
//
// VENUE SUMMARIES
//
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Each key of a "venue summaries" object is a VenueId, and each value contains possibly-translated summary information
 * for that Venue.
 *
 * The 'en' translation is the "base" translation and must be present; any other key is a translation
 * (for which there will translated Entity data)
 *
 *  venueSummaries = {
 *      ...
 *      dfw: {
 *          en: {
 *              airportCode: 'DFW',
 *              assetVersion: '2019-01-28T23:40:54',
 *              id: 'dfw',
 *              locale: 'en',
 *              name: 'Dallas/Fort Worth International Airport',
 *              isTranslated: false,
 *              localeDisplay: 'English'
 *          },
 *          es: {
 *              airportCode: 'DFW',
 *              assetVersion: '2019-01-28T23:41:08',
 *              id: 'dfwsp',
 *              locale: 'es',
 *              name: 'Aeropuerto Internacional de Dallas Fort Worth',
 *              isTranslated: true,
 *              localeDisplay: 'Spanish'
 *          }
 *      },
 *      ...
 *  }
 *
 * However, the format we want as output is an array of objects, one for each VenueId, the non-"base" translations
 * accumulated into a child "translations" field and additional information for:
 *
 *      uuid
 *      isV5
 *      venueCategory
 *
 *  [
 *      ...
 *      {
 *          airportCode: 'DFW',
 *          assetVersion: '2019-01-28T23:40:54',
 *          id: 'dfw',
 *          locale: 'en',
 *          name: '[TEST] Dallas/Fort Worth International Airport',
 *          isTranslated: false,
 *          localeDisplay: 'English',
 *          uuid: 'testdfw',
 *          isV5: false,
 *          venueCategory: 'v4gate',
 *          translations: [{
 *              airportCode: 'DFW',
 *              assetVersion: '2019-01-28T23:41:08',
 *              id: 'dfwsp',
 *              locale: 'es',
 *              name: '[TEST] Aeropuerto Internacional de Dallas Fort Worth',
 *              isTranslated: true,
 *              localeDisplay: 'Spanish',
 *              uuid: 'testdfwsp',
 *              isV5: false,
 *              venueCategory: 'v4gate'
 *          }]
 *      },
 *      ...
 *  ]
 *
 *
 * TODO: some venues are tests for which there is only "sheet" data and these have to be fixed up
 * using fakeEnrichVenueSummaryData
 */

/*
 * Load venue summaries to display in the VMS. A "venue summary" has much more information than the underlying
 * VenueList -- and it's idiosyncratic to the VMS, so we translate it here
 */
const getVenueSummaries = async () => {
    const state = getState();
    const translatedVenuesEnabled = state?.featureFlags?.translatedVenues;
    const accountId = baseLoader().accountId();
    const accountIds = R.is(Array, accountId) ? accountId : [accountId];
    const venues = translatedVenuesEnabled
        ? await Promise.all(R.map(baseLoader().listTranslatedVenuesForAccount, accountIds))
        : await Promise.all(R.map(baseLoader().listVenuesForAccount, accountIds));
    const venueSummaries = R.mergeAll(venues);
    return R.values(venueSummaries);
};

// Convert a canonicalName to a URL
const urlForBadge = (canonicalName, venueCategory) => {
    let category;
    switch (venueCategory) {
        case 'airport':
            category = venueCategory;
            break;
        case 'casino':
            category = venueCategory;
            break;
        case 'smartcampus':
            category = 'sw';
            break;
        default:
            category = 'airport';
    }
    const url = `https://content.locuslabs.com/vms-ui/venue-type-icons/${category}.square.svg`;
    return url;
};

// Return the url for the special Red X 'missing' badge
const urlForMissingBadge = () => urlForBadge('missing');

/**
 * Takes the changes and the IDs from uploading pixels for the new images and new logo and changing the images and logo
 * fields in the change object into something that can be used by the backend.
 */
const imageMangler = (changes, imagesIds, logoId, isV5) => {
    const getUuid = (type, index) => (type === 'logo' ? logoId : imagesIds[index]);

    // For each image object, change so that it only contains a caption, blobId, and id
    const filterProperties = (image = changes.logo, index, type = 'logo') => {
        const img = {
            alternativeText: image.alternativeText,
            caption: image.caption, // Either the caption (initially the image name) or an empty string for old images
            blobId: !R.isNil(image.blobId) ? image.blobId : getUuid(type, index), // Add blobId for new images
        };

        if (isV5 && image.id) img.id = image.id; // JUST UNTIL OFF SHEETS!!!

        if (!isV5) img.id = 'SHEET'; // JUST UNTIL OFF SHEETS!!!

        return img;
    };

    const filterArray = imageArray => imageArray.map((image, index) => filterProperties(image, index, 'images'));

    const tranformProperties = {
        images: R.isNil(changes.images) ? () => null : filterArray,
        logo: R.isNil(changes.logo) ? () => null : filterProperties,
    };
    const newChangeObj = R.evolve(tranformProperties, changes);

    // When deleting, set logo to null so prop is passed to RMS
    return R.has('logo', changes) ? newChangeObj : R.omit(['logo'], newChangeObj);
};

const getStyle = async (venueCategory, venueId, accountId) => {
    const sourceStyle = await baseLoader().getStyle(venueCategory, venueId);
    return sourceStyle ? convertStyle(sourceStyle, venueCategory, venueId, accountId) : null;
};

const loadMapStyle = venueCategory => async venueId => {
    const accountId = baseLoader().accountId();
    const style = await getStyle(venueCategory, venueId, accountId);
    return style;
};

const getSpritesForValidation = venueCategory => async venueId => {
    const accountId = baseLoader().accountId();

    return baseLoader().getContent(CONTENT_TYPES.SPRITESHEET, 'spritesheet.json', venueCategory, venueId, accountId);
};

/*
 * Get the latest status of a venue in Asset Generation
 * statusCode can be SUCCEEDED || FAILED || RUNNING || TIMED_OUT || ABORTED
 * If statusCode is empty it will get the latest result
 * The filter will find the latest step function with the corresponding status code
 * @sig getStatus :: (VenueId, String) -> Status
 */
const getPublishStatus = async (venueId, statusCode = '', interval) => {
    try {
        const status = await baseLoader().getPublishStatus(venueId, { statusFilter: statusCode });

        return {
            executionStatus: status.status,
            startDate: new Date(status.startDate),
            stopDate: new Date(status.stopDate),
        };
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error, token timed out', e);
        clearInterval(interval);
    }
};
// When the above function is called looking specifically for statusCode "SUCCEEDED", if
// Assets-api cannot find a recent successful run, and returns a 404
// This function returns 'undefined'

// ---------------------------------------------------------------------------------------------------------------------
//
// MapNotes
//
// ---------------------------------------------------------------------------------------------------------------------

// ---------------------------------------------------------------------------------------------------------------------
//
// Export
//
// ---------------------------------------------------------------------------------------------------------------------

const CanonicalLoader = {
    // simple pass-through
    accountId: () => baseLoader().accountId(),
    createPOI: (id, entity) => baseLoader().createPOI(id, entity),
    openLocation: (id, obj) => baseLoader().openLocation(id, obj),
    uploadPixels: (img, content, entityType) => baseLoader().uploadPixels(img, content, entityType),
    getBoundsPolygons: (venueId, opts) => baseLoader().getBoundsPolygons(venueId, opts),
    getBoundingBoxes: (venueId, opts) => baseLoader().getBoundingBoxes(venueId, opts),
    getAiJson: (venueId, format = 'v5') => baseLoader().getAiJson(venueId, format),
    publish: venueId => baseLoader().publish(venueId),
    getDerivedPolicyForToken: () => baseLoader().getDerivedPolicyForToken(),
    generateSpritesheet: (category, venueId, accountId) =>
        baseLoader().generateSpritesheet(category, venueId, accountId),
    getBlob: img => baseLoader().getBlob(img),
    getMapNoteTotals: venueId => baseLoader().getMapNoteTotals(venueId),
    getMapNotelets: venueId => baseLoader().getMapNotelets(venueId),
    getSelectedMapNote: (venueId, noteId) => baseLoader().getSelectedMapNote(venueId, noteId),
    deleteMapNote: (venueId, email, noteId) => baseLoader().deleteMapNote(venueId, noteId, email),
    updateMapNote: (venueId, noteId, changes) => baseLoader().updateMapNote(venueId, noteId, changes),
    addMapNoteComment: (venueId, email, noteId, comment) =>
        baseLoader().addMapNoteComment(venueId, email, noteId, comment),
    createMapNote: (venueId, email, newNote) => baseLoader().createMapNote(venueId, email, newNote),
    deleteMapNoteComment: (venueId, email, noteId, commentId) =>
        baseLoader().deleteMapNoteComment(venueId, email, noteId, commentId),
    putFeaturesAndEntities: (venueId, entities, options) =>
        baseLoader().putFeaturesAndEntities(venueId, entities, options),
    getEntities,
    getFeaturesForOrdinal,
    getFeaturesForVenueBackground,
    getPublishStatus,
    getTheme,
    getVenueSummaries,
    urlForBadge,
    urlForMissingBadge,
    imageMangler,
    listPOIs,
    loadMapStyle,
    initializeLoader,
    getSpritesForValidation,
    getOrdinalsForVenue,
    getStructuresForVenue,
    getLevelsForVenue,
    getVenue,
    setFilePaths,
    getIsMigrated,
    getInsightsAnalyticsConfig,
};

export default CanonicalLoader;
